import { format } from "date-fns";
import { DropdownMenu, Icon, Typography } from "@/components/atoms";
import { DataTableColumnDef } from "@/components/molecules";
import { PricingProfile } from "@/redux/slices/trip/types";
import { ContractPricingProfilesTableData } from "./types";

export const generateColumns = () => {
  const columns: DataTableColumnDef<ContractPricingProfilesTableData>[] = [
    {
      accessorKey: "yourPricingProfiles",
      header: "Your Pricing Profiles",
      cell: (props) => props.row.original.yourPricingProfiles,
      id: "name",
    },
    {
      accessorKey: "zoneRates",
      header: "Zone Rates",
      cell: (props) => props.row.original.zoneRates,
      size: 160,
      enableSorting: false,
    },
    {
      accessorKey: "stateRates",
      header: "State Rates",
      cell: (props) => props.row.original.stateRates,
      size: 160,
      enableSorting: false,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (props) => props.row.original.status,
      size: 160,
      id: "status",
    },
    {
      accessorKey: "lastEdited",
      header: "Last Edited",
      cell: (props) => props.row.original.lastEdited,
      size: 200,
      id: "updated_at",
    },
    {
      accessorKey: "action",
      header: "",
      cell: (props) => props.row.original.action,
      size: 75,
      enableSorting: false,
    },
  ];

  return columns;
};

export const generateData = (
  profiles: PricingProfile[],
  onManage: (profile: PricingProfile) => void,
  onDelete: (profile: PricingProfile) => void,
  dateFormat: string
) => {
  const data = profiles.map((profile) => {
    return {
      yourPricingProfiles: profile.name,
      zoneRates: renderRatesDisplay(profile.hasLocationPricing),
      stateRates: renderRatesDisplay(profile.hasStatePricing),
      status: profile.status ? renderStatusDisplay(profile.status) : "-",
      lastEdited: profile.updatedAt ? format(new Date(profile.updatedAt), dateFormat) : "-",
      action: (
        <DropdownMenu
          button={<Icon name="options" isCustom size="lg" className="text-neutral-dark-gray" />}
          position="bottom-start"
          className="!flex max-w-[24px]"
        >
          <DropdownMenu.Item onClick={() => onManage(profile)}>
            <Icon name="ArrowRight" className="mr-2 text-neutral-dark-gray" size="md" />
            <Typography variant="paragraph">Manage Pricing Profile</Typography>
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={() => onDelete(profile)}>
            <Icon name="Trash" className="mr-2 text-neutral-dark-gray" size="md" />
            <Typography variant="paragraph">Delete Pricing Profile</Typography>
          </DropdownMenu.Item>
        </DropdownMenu>
      ),
    } as ContractPricingProfilesTableData;
  });
  return data;
};

const renderRatesDisplay = (isPricingSet: boolean) => {
  if (isPricingSet) {
    return (
      <div className="flex flex-row items-center gap-2">
        <Icon size="md" name="TickCircle" className="flex-shrink-0 text-success" />
        <Typography variant="paragraph">Pricing Set</Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center gap-2">
      <Icon size="md" name="Danger" className="flex-shrink-0 text-warning" />
      <Typography variant="paragraph">Not Set</Typography>
    </div>
  );
};

const renderStatusDisplay = (status: string) => {
  if (status === "active") {
    return (
      <div className="flex flex-row items-center gap-2">
        <Icon size="md" name="TickCircle" variant="Bold" className="flex-shrink-0 text-success" />
        <Typography variant="paragraph">Active</Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center gap-2">
      <Icon size="md" name="CloseCircle" variant="Bold" className="flex-shrink-0 text-neutral-dark-gray" />
      <Typography variant="paragraph">Inactive</Typography>
    </div>
  );
};

export const getPaginationLabel = (page: number, pageSize: number, totalItems: number) => {
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  return `${start}-${end} of ${totalItems}`;
};
